"use client";

import { c as _c } from "react/compiler-runtime";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ChatBubbleLeftRightIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getRandomColorWithSeed, numberWithCommas } from "@/helpers/Helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import WaveItemFollow from "./WaveItemFollow";
const LEVEL_CLASSES: ReadonlyArray<{
  readonly minLevel: number;
  readonly classes: string;
}> = [{
  minLevel: 80,
  classes: "tw-text-[#55B075] tw-ring-[#55B075]"
}, {
  minLevel: 60,
  classes: "tw-text-[#AABE68] tw-ring-[#AABE68]"
}, {
  minLevel: 40,
  classes: "tw-text-[#DAC660] tw-ring-[#DAC660]"
}, {
  minLevel: 20,
  classes: "tw-text-[#DAAC60] tw-ring-[#DAAC60]"
}, {
  minLevel: 0,
  classes: "tw-text-[#DA8C60] tw-ring-[#DA8C60]"
}];
const DEFAULT_LEVEL_CLASS = LEVEL_CLASSES.at(-1)?.classes ?? "";
const CARD_BASE_CLASSES = "tw-@container/wave tw-group tw-rounded-xl tw-bg-iron-950 tw-backdrop-blur-sm tw-shadow-sm tw-shadow-black/20 tw-transition-all tw-duration-300 tw-ease-out";
const CARD_INTERACTIVE_CLASSES = "tw-cursor-pointer desktop-hover:hover:tw-shadow-lg desktop-hover:hover:tw-shadow-black/40 desktop-hover:hover:tw-translate-y-[-1px] focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-900 focus-visible:tw-outline-none";
const INTERACTIVE_TAGS = new Set(["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA", "LABEL"]);
const shouldSkipNavigation = (target: HTMLElement | null, root: HTMLElement): boolean => {
  let current: HTMLElement | null = target;
  while (current) {
    if (current === root) {
      break;
    }
    if (current.dataset["waveItemInteractive"] === "true") {
      return true;
    }
    if (INTERACTIVE_TAGS.has(current.tagName)) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
};
type CardContainerProps = {
  readonly isInteractive: boolean;
  readonly href?: string | undefined;
  readonly ariaLabel?: string | undefined;
  readonly children: ReactNode;
  readonly onClick?: ((event: MouseEvent<HTMLAnchorElement>) => void) | undefined;
  readonly onKeyDown?: ((event: KeyboardEvent<HTMLAnchorElement>) => void) | undefined;
};
function CardContainer(t0) {
  const $ = _c(13);
  const {
    isInteractive,
    href,
    ariaLabel,
    onClick,
    onKeyDown,
    children
  } = t0;
  const className = `${CARD_BASE_CLASSES} ${isInteractive ? CARD_INTERACTIVE_CLASSES : ""} tw-no-underline`;
  if (isInteractive && href) {
    let t1;
    if ($[0] !== onClick) {
      t1 = onClick ? {
        onClick
      } : {};
      $[0] = onClick;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== ariaLabel || $[3] !== children || $[4] !== className || $[5] !== href || $[6] !== onKeyDown || $[7] !== t1) {
      t2 = <Link href={href} prefetch={false} className={className} aria-label={ariaLabel} {...t1} onKeyDown={onKeyDown}>{children}</Link>;
      $[2] = ariaLabel;
      $[3] = children;
      $[4] = className;
      $[5] = href;
      $[6] = onKeyDown;
      $[7] = t1;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  let t1;
  if ($[9] !== ariaLabel || $[10] !== children || $[11] !== className) {
    t1 = <div className={className} aria-label={ariaLabel}>{children}</div>;
    $[9] = ariaLabel;
    $[10] = children;
    $[11] = className;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  return t1;
}
function getCardLabel(href?: string, label?: string | null) {
  if (!href) {
    return undefined;
  }
  return label ? `View wave ${label}` : "View wave";
}
function resolveLevelClasses(level?: number | null) {
  return LEVEL_CLASSES.find(levelClass => levelClass.minLevel <= (level ?? 0))?.classes ?? DEFAULT_LEVEL_CLASS;
}
export default function WaveItemWide(t0) {
  const $ = _c(71);
  const {
    wave,
    userPlaceholder,
    titlePlaceholder
  } = t0;
  const router = useRouter();
  const author = wave?.author;
  const authorHref = author?.handle ? `/${author.handle}` : undefined;
  const authorLevel = author?.level ?? 0;
  let t1;
  if ($[0] !== author?.banner1_color || $[1] !== author?.handle || $[2] !== userPlaceholder) {
    t1 = author?.banner1_color ?? getRandomColorWithSeed(author?.handle ?? userPlaceholder ?? "");
    $[0] = author?.banner1_color;
    $[1] = author?.handle;
    $[2] = userPlaceholder;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const banner1 = t1;
  let t2;
  if ($[4] !== author?.banner2_color || $[5] !== author?.handle || $[6] !== userPlaceholder) {
    t2 = author?.banner2_color ?? getRandomColorWithSeed(author?.handle ?? userPlaceholder ?? "");
    $[4] = author?.banner2_color;
    $[5] = author?.handle;
    $[6] = userPlaceholder;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const banner2 = t2;
  const isDirectMessage = wave?.chat.scope.group?.is_direct_message ?? false;
  let t3;
  let waveHref;
  if ($[8] !== isDirectMessage || $[9] !== wave) {
    waveHref = wave ? getWaveRoute({
      waveId: wave.id,
      isDirectMessage,
      isApp: false
    }) : undefined;
    const labelValue = wave?.name ?? wave?.id;
    t3 = getCardLabel(waveHref, labelValue);
    $[8] = isDirectMessage;
    $[9] = wave;
    $[10] = t3;
    $[11] = waveHref;
  } else {
    t3 = $[10];
    waveHref = $[11];
  }
  const cardLabel = t3;
  const isInteractive = Boolean(waveHref);
  let t4;
  if ($[12] !== author) {
    t4 = author?.pfp ? <Image src={getScaledImageUri(author.pfp, ImageScale.W_200_H_200)} alt={`${author.handle ?? author.primary_address} avatar`} width={28} height={28} className="tw-h-7 tw-w-7 tw-rounded-md tw-bg-iron-800 tw-object-cover tw-ring-1 tw-ring-white/10" /> : <div className="tw-h-7 tw-w-7 tw-rounded-md tw-bg-iron-800 tw-ring-1 tw-ring-white/10" />;
    $[12] = author;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const authorAvatar = t4;
  const t5 = author?.level;
  let t6;
  if ($[14] !== t5) {
    t6 = resolveLevelClasses(t5);
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = `${t6} tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-xl tw-border-none tw-bg-transparent tw-px-2 tw-py-0.5 tw-text-[0.625rem] tw-font-semibold tw-leading-3 tw-ring-2 tw-ring-inset`;
  let t8;
  if ($[16] !== authorLevel || $[17] !== t7) {
    t8 = <div className={t7}>Level {authorLevel}</div>;
    $[16] = authorLevel;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const authorLevelBadge = t8;
  let t9;
  if ($[19] !== authorHref || $[20] !== router) {
    t9 = event => {
      if (!authorHref) {
        return;
      }
      if (event.metaKey || event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();
        window.open(authorHref, "_blank", "noopener,noreferrer");
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      router.push(authorHref);
    };
    $[19] = authorHref;
    $[20] = router;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleAuthorClick = t9;
  let t10;
  if ($[22] !== authorHref) {
    t10 = event_0 => {
      if (!authorHref || event_0.button !== 1) {
        return;
      }
      event_0.preventDefault();
      event_0.stopPropagation();
      window.open(authorHref, "_blank", "noopener,noreferrer");
    };
    $[22] = authorHref;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const handleAuthorAuxClick = t10;
  let t11;
  if ($[24] !== author || $[25] !== authorAvatar || $[26] !== authorHref || $[27] !== authorLevelBadge || $[28] !== handleAuthorAuxClick || $[29] !== handleAuthorClick || $[30] !== userPlaceholder) {
    t11 = authorHref ? <button type="button" data-wave-item-interactive="true" onClick={handleAuthorClick} onAuxClick={handleAuthorAuxClick} className="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-text-left" aria-label={author?.handle ? `View @${author.handle}` : "View author profile"}>{authorAvatar}<span className="tw-truncate tw-text-xs tw-font-semibold tw-text-white">{author?.handle ?? userPlaceholder}</span>{authorLevelBadge}</button> : <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">{authorAvatar}<span className="tw-truncate tw-text-xs tw-font-semibold tw-text-white">{author?.handle ?? userPlaceholder}</span>{authorLevelBadge}</div>;
    $[24] = author;
    $[25] = authorAvatar;
    $[26] = authorHref;
    $[27] = authorLevelBadge;
    $[28] = handleAuthorAuxClick;
    $[29] = handleAuthorClick;
    $[30] = userPlaceholder;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const authorSection = t11;
  let t12;
  if ($[32] !== router || $[33] !== waveHref) {
    t12 = event_1 => {
      if (!waveHref) {
        return;
      }
      const target = event_1.target as HTMLElement | null;
      if (shouldSkipNavigation(target, event_1.currentTarget)) {
        event_1.preventDefault();
        event_1.stopPropagation();
        return;
      }
      if (event_1.button === 1 || event_1.metaKey || event_1.ctrlKey) {
        return;
      }
      if (!event_1.defaultPrevented) {
        event_1.preventDefault();
        router.push(waveHref);
      }
    };
    $[32] = router;
    $[33] = waveHref;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const handleCardClick = t12;
  let t13;
  if ($[35] !== router || $[36] !== waveHref) {
    t13 = event_2 => {
      if (!waveHref || event_2.target !== event_2.currentTarget) {
        return;
      }
      if (event_2.key === "Enter" || event_2.key === " " || event_2.code === "Space") {
        event_2.preventDefault();
        router.push(waveHref);
      }
    };
    $[35] = router;
    $[36] = waveHref;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const handleCardKeyDown = t13;
  const dropsCount = wave?.metrics.drops_count ?? 0;
  const subscribersCount = wave?.metrics.subscribers_count ?? 0;
  const t14 = `linear-gradient(45deg, ${banner1} 0%, ${banner2} 100%)`;
  const t15 = wave?.picture ? 0.35 : 1;
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <div className="tw-absolute tw-inset-0" style={{
      background: t14,
      opacity: t15
    }} />;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== wave) {
    t17 = wave?.picture && <Image src={getScaledImageUri(wave.picture, ImageScale.AUTOx450)} alt={`Wave ${wave.name}`} fill={true} sizes="(max-width: 639px) 96px, (max-width: 1023px) 112px, 128px" className="tw-object-cover tw-transition-transform tw-duration-500 tw-will-change-transform desktop-hover:group-hover:tw-scale-[1.02]" />;
    $[41] = wave;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t16 || $[44] !== t17) {
    t18 = <div className="tw-relative tw-aspect-[4/3] tw-w-24 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-lg tw-bg-iron-900 sm:tw-w-28 md:tw-w-32">{t16}{t17}</div>;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const t19 = wave?.name ?? titlePlaceholder;
  let t20;
  if ($[46] !== t19) {
    t20 = <span className="tw-line-clamp-2 tw-text-sm tw-font-bold tw-leading-tight tw-text-white sm:tw-text-base">{t19}</span>;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== wave) {
    t21 = wave && <div data-wave-item-interactive="true" className="tw-pointer-events-auto tw-relative tw-z-10"><WaveItemFollow wave={wave} /></div>;
    $[48] = wave;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t20 || $[51] !== t21) {
    t22 = <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">{t20}{t21}</div>;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== dropsCount || $[54] !== subscribersCount || $[55] !== wave) {
    t23 = wave && <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-[11px] tw-text-iron-400"><span className="tw-inline-flex tw-items-center tw-gap-1"><ChatBubbleLeftRightIcon aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-iron-400" /><span className="tw-font-medium">{numberWithCommas(dropsCount)}</span><span className="tw-text-iron-500">{dropsCount === 1 ? "Drop" : "Drops"}</span></span><span className="tw-inline-flex tw-items-center tw-gap-1"><UserGroupIcon aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-iron-400" /><span className="tw-font-medium">{numberWithCommas(subscribersCount)}</span><span className="tw-text-iron-500">Joined</span></span></div>;
    $[53] = dropsCount;
    $[54] = subscribersCount;
    $[55] = wave;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== authorSection || $[58] !== t22 || $[59] !== t23) {
    t24 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">{t22}{authorSection}{t23}</div>;
    $[57] = authorSection;
    $[58] = t22;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t18 || $[62] !== t24) {
    t25 = <div className="tw-flex tw-gap-3 tw-p-3 sm:tw-p-4">{t18}{t24}</div>;
    $[61] = t18;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== cardLabel || $[65] !== handleCardClick || $[66] !== handleCardKeyDown || $[67] !== isInteractive || $[68] !== t25 || $[69] !== waveHref) {
    t26 = <CardContainer isInteractive={isInteractive} href={waveHref} ariaLabel={cardLabel} onClick={handleCardClick} onKeyDown={handleCardKeyDown}>{t25}</CardContainer>;
    $[64] = cardLabel;
    $[65] = handleCardClick;
    $[66] = handleCardKeyDown;
    $[67] = isInteractive;
    $[68] = t25;
    $[69] = waveHref;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  return t26;
}